import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Tag,
  Typography,
  Card,
  InputNumber,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { userApi } from '../../services/api';
import { User, CreateUserRequest, UpdateUserRequest, AdjustBalanceRequest } from '../../types';
import type { ColumnsType } from 'antd/es/table';
import { usePermissions, usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';

const { Title } = Typography;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // 模态框状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [balanceModalVisible, setBalanceModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 表单实例
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [balanceForm] = Form.useForm();

  // 权限检查
  const { loading: permissionLoading } = usePermissions();
  const { canViewUsers, canCreateUsers, canEditUsers, canDeleteUsers } = usePermissionCheck();

  useEffect(() => {
    if (!permissionLoading && canViewUsers()) {
      fetchUsers();
    }
  }, [permissionLoading, current, pageSize]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getUsers({ page: current, page_size: pageSize });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (values: CreateUserRequest) => {
    try {
      await userApi.createUser(values);
      message.success('创建用户成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchUsers();
    } catch (error) {
      message.error('创建用户失败');
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      name: user.name,
      is_admin: user.is_admin,
    });
    setEditModalVisible(true);
  };

  const handleUpdateUser = async (values: UpdateUserRequest) => {
    if (!selectedUser) return;
    
    try {
      await userApi.updateUser(selectedUser.id, values);
      message.success('更新用户成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      message.error('更新用户失败');
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await userApi.deleteUser(id);
      message.success('删除用户成功');
      fetchUsers();
    } catch (error) {
      message.error('删除用户失败');
    }
  };

  const handleAdjustBalance = (user: User) => {
    setSelectedUser(user);
    balanceForm.resetFields();
    setBalanceModalVisible(true);
  };

  const handleBalanceSubmit = async (values: AdjustBalanceRequest) => {
    if (!selectedUser) return;
    
    try {
      await userApi.adjustBalance(selectedUser.id, values);
      message.success('余额调整成功');
      setBalanceModalVisible(false);
      balanceForm.resetFields();
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      message.error('余额调整失败');
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '用户姓名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number) => {
        const balanceValue = balance ?? 0;
        return (
          <span style={{ color: balanceValue > 0 ? '#52c41a' : '#f5222d' }}>
            ¥{balanceValue.toFixed(2)}
          </span>
        );
      },
      sorter: (a, b) => (a.balance ?? 0) - (b.balance ?? 0),
    },
    {
      title: '管理员',
      dataIndex: 'is_admin',
      key: 'is_admin',
      render: (isAdmin: boolean) => (
        <Tag color={isAdmin ? 'red' : 'default'}>
          {isAdmin ? '是' : '否'}
        </Tag>
      ),
      filters: [
        { text: '管理员', value: true },
        { text: '普通用户', value: false },
      ],
      onFilter: (value, record) => record.is_admin === value,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => {
        if (!date) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return new Date(date).toLocaleString('zh-CN');
      },
      sorter: (a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <PermissionGuard permission="user.edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            >
              编辑
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="user.edit">
            <Button
              type="link"
              icon={<DollarOutlined />}
              onClick={() => handleAdjustBalance(record)}
            >
              调整余额
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="user.delete">
            <Popconfirm
              title="确定要删除这个用户吗？"
              onConfirm={() => handleDeleteUser(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  // 权限检查失败时的展示
  if (!canViewUsers()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看用户管理的权限</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2}>用户管理</Title>
          <PermissionGuard permission="user.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              创建用户
            </Button>
          </PermissionGuard>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size || 10);
            },
          }}
        />
      </Card>

      {/* 创建用户模态框 */}
      <Modal
        title="创建用户"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="name"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="username"
            label="用户姓名"
            rules={[{ required: true, message: '请输入用户姓名' }]}
          >
            <Input placeholder="请输入用户姓名" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱" autoComplete="email" />
          </Form.Item>
          <Form.Item
            name="is_admin"
            label="管理员权限"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户模态框 */}
      <Modal
        title="编辑用户"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="name"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="is_admin"
            label="管理员权限"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                更新
              </Button>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setSelectedUser(null);
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 调整余额模态框 */}
      <Modal
        title={`调整用户余额 - ${selectedUser?.name}`}
        open={balanceModalVisible}
        onCancel={() => {
          setBalanceModalVisible(false);
          balanceForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text>当前余额: </Typography.Text>
          <Typography.Text strong style={{ color: selectedUser && selectedUser.balance > 0 ? '#52c41a' : '#f5222d' }}>
            ¥{selectedUser?.balance.toFixed(2)}
          </Typography.Text>
        </div>
        <Divider />
        <Form
          form={balanceForm}
          layout="vertical"
          onFinish={handleBalanceSubmit}
        >
          <Form.Item
            name="amount"
            label="调整金额"
            rules={[
              { required: true, message: '请输入调整金额' },
              { type: 'number', message: '请输入有效数字' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="正数为充值，负数为扣费"
              precision={2}
              step={0.01}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="调整原因"
            rules={[{ required: true, message: '请输入调整原因' }]}
          >
            <Input.TextArea placeholder="请输入调整原因" rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认调整
              </Button>
              <Button onClick={() => {
                setBalanceModalVisible(false);
                balanceForm.resetFields();
                setSelectedUser(null);
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement; 